import type * as THREE from 'three';
// packages/character-render/src/humanoidRig.ts
//
// Rig vocabulary behind <HumanoidMesh>: the part/voxel input shapes, the bone
// set, the part-name -> bone map and the rig output types. Split out of
// humanoidRig.ts (which re-exports everything here and keeps the builder).
export interface CharacterVoxel {
  x: number;
  y: number;
  z: number;
  w?: number;
  h?: number;
  d?: number;
  color: string;
}

export interface CharacterPart {
  name:
    | 'head'
    | 'torso'
    | 'body'
    | 'left_arm'
    | 'right_arm'
    | 'left_leg'
    | 'right_leg'
    | 'front_left_leg'
    | 'front_right_leg'
    | 'back_left_leg'
    | 'back_right_leg'
    | 'misc'
    | 'left_wing'
    | 'right_wing'
    | 'tail'
    | 'left_upper_arm'
    | 'left_lower_arm'
    | 'left_hand'
    | 'right_upper_arm'
    | 'right_lower_arm'
    | 'right_hand'
    | 'left_upper_leg'
    | 'left_lower_leg'
    | 'left_foot'
    | 'right_upper_leg'
    | 'right_lower_leg'
    | 'right_foot'
    | 'front_left_upper_leg'
    | 'front_left_lower_leg'
    | 'front_left_foot'
    | 'front_right_upper_leg'
    | 'front_right_lower_leg'
    | 'front_right_foot'
    | 'back_left_upper_leg'
    | 'back_left_lower_leg'
    | 'back_left_foot'
    | 'back_right_upper_leg'
    | 'back_right_lower_leg'
    | 'back_right_foot'
    | 'mid_left_upper_leg'
    | 'mid_left_lower_leg'
    | 'mid_left_foot'
    | 'mid_right_upper_leg'
    | 'mid_right_lower_leg'
    | 'mid_right_foot'
    | 'rear_left_upper_leg'
    | 'rear_left_lower_leg'
    | 'rear_left_foot'
    | 'rear_right_upper_leg'
    | 'rear_right_lower_leg'
    | 'rear_right_foot'
    | 'neck'
    | 'left_horn'
    | 'right_horn'
    | 'spine_1'
    | 'spine_2'
    | 'spine_3'
    | 'spine_4'
    | 'spine_5'
    | 'snake_segment';
  voxels: CharacterVoxel[];
  scale?: [number, number, number];
  offset?: [number, number, number];
  rotation?: [number, number, number];
  bone?: string;
  roughness?: number;
  metalness?: number;
}

/** Rigid bones a humanoid part can ride on. Head and arms hang off the body. */
export type HumanoidBone =
  | 'root'
  | 'body'
  | 'head'
  | 'left_arm'
  | 'right_arm'
  | 'left_leg'
  | 'right_leg';

export const HUMANOID_BONES: readonly HumanoidBone[] = [
  'root',
  'body',
  'head',
  'left_arm',
  'right_arm',
  'left_leg',
  'right_leg',
];

/** Optional surface + rig hints a part may carry on top of `CharacterPart`. */
export interface HumanoidPartStyle {
  /** Bone the part rides on. Default: derived from the part name. */
  bone?: HumanoidBone;
  /** Surface roughness 0..1. Default: per bone (skin 0.6, cloth 0.85, boots 0.9). */
  roughness?: number;
  /** Surface metalness 0..1. Default 0. */
  metalness?: number;
  /** Emissive colour (e.g. a lit lantern or glowing eyes). Default none. */
  emissive?: string;
}

export type HumanoidPart = CharacterPart & HumanoidPartStyle;

export const BONE_INDEX: Record<HumanoidBone, number> = {
  root: 0,
  body: 1,
  head: 2,
  left_arm: 3,
  right_arm: 4,
  left_leg: 5,
  right_leg: 6,
};

/** Map a part name onto a bone. Quadruped/creature limbs stay on the root. */
export function boneForPart(name: string): HumanoidBone {
  if (name === 'head' || name === 'neck' || name === 'left_horn' || name === 'right_horn')
    return 'head';
  if (
    name === 'left_arm' ||
    name === 'left_upper_arm' ||
    name === 'left_lower_arm' ||
    name === 'left_hand'
  )
    return 'left_arm';
  if (
    name === 'right_arm' ||
    name === 'right_upper_arm' ||
    name === 'right_lower_arm' ||
    name === 'right_hand'
  )
    return 'right_arm';
  if (
    name === 'left_leg' ||
    name === 'left_upper_leg' ||
    name === 'left_lower_leg' ||
    name === 'left_foot'
  )
    return 'left_leg';
  if (
    name === 'right_leg' ||
    name === 'right_upper_leg' ||
    name === 'right_lower_leg' ||
    name === 'right_foot'
  )
    return 'right_leg';
  if (
    name.includes('leg') ||
    name.includes('foot') ||
    name === 'tail' ||
    name.startsWith('spine') ||
    name === 'snake_segment'
  ) {
    return 'root';
  }
  return 'body';
}

export const DEFAULT_ROUGHNESS: Record<HumanoidBone, number> = {
  root: 0.85,
  body: 0.85,
  head: 0.6,
  left_arm: 0.8,
  right_arm: 0.8,
  left_leg: 0.9,
  right_leg: 0.9,
};

/** Surface a batch of instances shares. Colours ride on instanceColor. */
export interface SurfaceKey {
  roughness: number;
  metalness: number;
  emissive: string | null;
}

export interface RigBatch {
  /** Stable id: surface + count, so React remounts the InstancedMesh when the count changes. */
  key: string;
  surface: SurfaceKey;
  count: number;
  /** Rest-pose matrix per instance (part transform × voxel translation). */
  rest: THREE.Matrix4[];
  /** Bone index per instance (see HUMANOID_BONES). */
  bones: Uint8Array;
  /** Linear-space colour per instance. */
  colors: THREE.Color[];
}

export interface HumanoidRig {
  batches: RigBatch[];
  /** Pivot per bone in figure space (HUMANOID_BONES order). */
  pivots: THREE.Vector3[];
  /** Figure height (max y − min y); the poses scale their bob by it. */
  height: number;
  /** Lowest point of the figure (the feet). */
  minY: number;
  instanceCount: number;
}
